import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { type OfferFormValue } from "@/pages/offer-form";
import { normalizeOfferInput, countRecognizedOfferKeys } from "@/lib/offer-normalize";

interface Props {
  onImport: (next: OfferFormValue) => void;
  current: OfferFormValue;
}

export function OfferJsonImportDialog({ onImport, current }: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const { toast } = useToast();

  const handleImport = () => {
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch (e: any) {
      toast({ title: "Invalid JSON", description: e.message, variant: "destructive" });
      return;
    }
    const count = countRecognizedOfferKeys(parsed);
    if (count === 0) {
      toast({ title: "Nothing to import", description: "JSON had no recognizable offer fields.", variant: "destructive" });
      return;
    }
    // Merge over the current values + empty template so missing groups stay
    // valid for the form and enum fields are coerced to allowed values.
    const next = normalizeOfferInput(parsed, current);
    onImport(next);
    setOpen(false);
    setText("");
    toast({ title: "Imported", description: `Filled ${count} field group(s).` });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="button-import-json">
          <Upload className="h-4 w-4 mr-2" /> Import JSON
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import offer from JSON</DialogTitle>
          <DialogDescription>
            Paste an offer JSON object below. Supports either snake_case (e.g. <code>buyer_problem</code>) or camelCase (<code>buyerProblem</code>) keys, and accepts either the offer object directly or an envelope like <code>{`{ "offer": { ... } }`}</code>.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={16}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='{"name": "...", "offer_type": "service", "buyer_problem": {...}, ...}'
          data-testid="input-import-json"
          className="font-mono text-xs"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="button-import-cancel">Cancel</Button>
          <Button onClick={handleImport} disabled={!text.trim()} data-testid="button-import-confirm">Import</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
